import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AnimatePresence } from 'framer-motion';
import {
  Button,
  Text,
  Flex,
  LoadingOrbit,
  useToast,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
} from '@chatsdet/ui';
import {
  Gear,
  Play,
  Plus,
  X,
  FloppyDisk,
  Warning,
  ArrowUUpLeft,
  Rows,
  SquaresFour,
} from '@phosphor-icons/react';
import { Board, type BoardColumnConfig, type BoardDropEvent } from '../../components/board';
import { AddTestsSearch, AUTO_RUN_FLOW_TYPES, type AddTagOption } from './AddTestsSearch';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useEligibleWorkflowSummaries } from '../../../hooks/useWorkflows';
import {
  useTestSuite,
  useReplaceSuiteMembers,
  useUpdateTestSuite,
  useRunTestSuite,
  RunNativeSuiteDialog,
  useArrangementHistory,
  type SuiteFailureMode,
  type TestSuiteMemberDetail,
} from '../../../features/test-suites';
import { ScheduleSuiteModal } from '../../../features/test-suites/ScheduleSuiteModal';
import { SuiteTestCard } from './SuiteTestCard';
import { SuiteSettingsOverlay } from './SuiteSettingsOverlay';
import { ExternalSuiteConfigPage } from './external/ExternalSuiteConfigPage';

const STAGING = 'staging';

/** Where the add palette deposits tests. */
type AddTarget = { kind: 'layer'; index: number } | { kind: 'staging' };

/** Circled layer number + "Layer N" + parallel/empty subtext for a band header. */
function LayerHeader({
  index,
  count,
  seeds,
}: {
  index: number;
  count: number;
  seeds?: { workflowId: string; workflowName: string }[];
}) {
  return (
    <Flex align="center" gap="sm" className="min-w-0">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
        {index + 1}
      </span>
      <Text weight="semibold" size="sm">
        Layer {index + 1}
      </Text>
      <Text size="xs" className="truncate text-muted-foreground">
        {count === 0 ? 'empty' : `${count} test${count === 1 ? '' : 's'} · run in parallel`}
      </Text>
      {seeds && seeds.length > 0 && (
        <span
          className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
          title={`Layer seed${seeds.length === 1 ? '' : 's'} provisioned before this layer and torn down after: ${seeds
            .map((s) => s.workflowName)
            .join(', ')}`}
        >
          {seeds.length} seed{seeds.length === 1 ? '' : 's'}
        </span>
      )}
    </Flex>
  );
}

/** Staging rail header: title + count, with a persistent "won't run" caption. */
function StagingHeader({ count }: { count: number }) {
  return (
    <div className="min-w-0">
      <Flex align="center" gap="sm">
        <Text weight="semibold" size="sm">
          Staging
        </Text>
        <Text size="xs" className="text-muted-foreground">
          {count === 0 ? 'empty' : `${count} test${count === 1 ? '' : 's'}`}
        </Text>
      </Flex>
      <Text size="xs" className="text-muted-foreground/70">
        Won&rsquo;t run until added to a layer
      </Text>
    </div>
  );
}

/**
 * A read-only band for suite-scoped seeds (run before the layers) or teardowns
 * (run after). Derived from member node configs — informational, not editable:
 * dashed cards, no drag/add/remove.
 */
function ReadOnlyBand({
  pill,
  hint,
  items,
}: {
  pill: string;
  hint: string;
  items: { workflowId: string; workflowName: string }[];
}) {
  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center gap-2 px-1 pb-1.5">
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {pill}
        </span>
        <Text size="xs" className="truncate text-muted-foreground">
          {hint}
        </Text>
      </div>
      <div className="border-t border-dashed border-border" />
      <div className="flex min-h-[84px] items-stretch gap-3 overflow-x-auto px-1 pb-1 pt-3">
        {items.map((it) => (
          <div
            key={it.workflowId}
            className="flex w-64 shrink-0 flex-col justify-center gap-1 rounded-lg border border-dashed border-border bg-muted/30 p-3"
          >
            <Text size="sm" weight="medium" className="line-clamp-2 text-muted-foreground">
              {it.workflowName}
            </Text>
            <Text size="xs" className="text-muted-foreground/60">
              Runs automatically · read-only
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SuiteBuilderPage() {
  const { suiteId } = useParams({ from: '/suites/$suiteId' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const suiteQuery = useTestSuite(suiteId);
  const summaries = useEligibleWorkflowSummaries(true, { withTags: true, withRuns: true });
  const saveMembers = useReplaceSuiteMembers(suiteId);
  const updateSuite = useUpdateTestSuite(suiteId);
  const runSuite = useRunTestSuite();

  const {
    present,
    apply,
    undo,
    canUndo,
    dirty: layoutDirty,
    reset,
    markSaved,
  } = useArrangementHistory();

  const [pickerTarget, setPickerTarget] = React.useState<AddTarget | null>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [confirmDeleteLayer, setConfirmDeleteLayer] = React.useState<number | null>(null);
  const [runDialogOpen, setRunDialogOpen] = React.useState(false);

  // Local-editable settings
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [failureMode, setFailureMode] = React.useState<SuiteFailureMode>('continue');

  const promotedWorkflows = summaries.data?.workflows ?? [];
  // Read-only suite-scoped seeds (run before) and teardowns (run after).
  const suiteSeeds = suiteQuery.data?.suiteSeeds ?? [];
  const suiteTeardowns = suiteQuery.data?.suiteTeardowns ?? [];
  // Layer-scoped seeds, indexed by layer so each layer header shows its seed count.
  const layerSeedsByLayer = React.useMemo(() => {
    const m = new Map<number, { workflowId: string; workflowName: string }[]>();
    for (const group of suiteQuery.data?.layerSeeds ?? []) m.set(group.layerIndex, group.seeds);
    return m;
  }, [suiteQuery.data]);

  const nameMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const w of promotedWorkflows) m.set(w.id, w.name);
    for (const member of suiteQuery.data?.members ?? [])
      m.set(member.workflowId, member.workflowName);
    return m;
  }, [promotedWorkflows, suiteQuery.data]);

  const memberByWorkflowId = React.useMemo(() => {
    const m = new Map<string, TestSuiteMemberDetail>();
    for (const member of suiteQuery.data?.members ?? []) m.set(member.workflowId, member);
    return m;
  }, [suiteQuery.data]);

  const summaryByWorkflowId = React.useMemo(() => {
    const m = new Map<string, (typeof promotedWorkflows)[number]>();
    for (const w of promotedWorkflows) m.set(w.id, w);
    return m;
  }, [promotedWorkflows]);

  // Hydrate local state once the suite loads (or refetches after a save).
  React.useEffect(() => {
    if (!suiteQuery.data) return;
    const byLayer = new Map<number, string[]>();
    const staging: string[] = [];
    let maxLayer = -1;
    for (const member of suiteQuery.data.members) {
      if (member.layerIndex === null) {
        staging.push(member.workflowId);
        continue;
      }
      const arr = byLayer.get(member.layerIndex) ?? [];
      arr.push(member.workflowId);
      byLayer.set(member.layerIndex, arr);
      maxLayer = Math.max(maxLayer, member.layerIndex);
    }
    const layers: string[][] = [];
    for (let i = 0; i <= maxLayer; i += 1) layers.push(byLayer.get(i) ?? []);
    if (layers.length === 0) layers.push([]);
    reset({ layers, staging });
    setName(suiteQuery.data.name);
    setDescription(suiteQuery.data.description ?? '');
    setFailureMode(suiteQuery.data.failureMode);
  }, [suiteQuery.data, reset]);

  useBreadcrumbs([
    { key: 'suites', label: 'Suites', href: '/suites' },
    { key: 'builder', label: suiteQuery.data?.name ?? 'Suite' },
  ]);

  // Synthesize a member for any freshly-added workflow not yet present in the
  // enriched suite payload. Pull tags + run history from the eligible-workflow
  // summary (loaded with withTags/withRuns) so a just-added card shows its real
  // timeline, last-run time, and tags immediately — not "No runs yet" until the
  // suite is saved and refetched.
  const synth = React.useCallback(
    (wfId: string, layerIndex: number | null): TestSuiteMemberDetail => {
      const member = memberByWorkflowId.get(wfId);
      if (member) return member;
      const summary = summaryByWorkflowId.get(wfId);
      return {
        workflowId: wfId,
        workflowName: nameMap.get(wfId) ?? wfId,
        health: summary?.health ?? 'unverified',
        layerIndex,
        orderIndex: 0,
        tags: summary?.tags ?? [],
        recentRuns: summary?.recentRuns ?? [],
        lastRunAt: summary?.lastRunAt ?? null,
      };
    },
    [memberByWorkflowId, summaryByWorkflowId, nameMap]
  );

  const itemsByColumn = React.useMemo<Record<string, TestSuiteMemberDetail[]>>(() => {
    const out: Record<string, TestSuiteMemberDetail[]> = {
      [STAGING]: present.staging.map((id) => synth(id, null)),
    };
    present.layers.forEach((layer, i) => {
      out[String(i)] = layer.map((wfId) => synth(wfId, i));
    });
    return out;
  }, [present, synth]);

  const runMembers = React.useMemo(
    () => [
      ...present.layers.flatMap((layer, layerIndex) =>
        layer.map((workflowId) => ({
          ...synth(workflowId, layerIndex),
          layerIndex,
        }))
      ),
      ...present.staging.map((workflowId) => ({
        ...synth(workflowId, null),
        layerIndex: null,
      })),
    ],
    [present, synth]
  );

  const columnOfWorkflow = React.useMemo(() => {
    const m = new Map<string, string>();
    present.staging.forEach((wfId) => m.set(wfId, STAGING));
    present.layers.forEach((layer, i) => layer.forEach((wfId) => m.set(wfId, String(i))));
    return m;
  }, [present]);

  // Tests/tags not yet placed anywhere in the suite.
  const placed = React.useMemo(
    () => new Set([...present.layers.flat(), ...present.staging]),
    [present]
  );
  const testOptions = React.useMemo(
    () =>
      promotedWorkflows
        .filter((w) => !placed.has(w.id))
        .map((w) => ({ id: w.id, name: w.name, flowType: w.flowType })),
    [promotedWorkflows, placed]
  );
  const tagOptions = React.useMemo<AddTagOption[]>(() => {
    const byTag = new Map<string, AddTagOption>();
    for (const w of promotedWorkflows) {
      // Tag bulk-add only adds standard tests — auto-run scripts
      // (setup/seed/teardown) run automatically and aren't suite members.
      if (placed.has(w.id) || AUTO_RUN_FLOW_TYPES.has(w.flowType ?? '')) continue;
      for (const t of w.tags ?? []) {
        const entry = byTag.get(t.id) ?? {
          id: t.id,
          name: t.name,
          color: t.color,
          workflowIds: [],
        };
        entry.workflowIds.push(w.id);
        byTag.set(t.id, entry);
      }
    }
    return [...byTag.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [promotedWorkflows, placed]);

  // ── Layout mutations (all go through the undo history) ───────────────────
  const removeLayer = (idx: number) =>
    apply((prev) => ({ ...prev, layers: prev.layers.filter((_, i) => i !== idx) }));

  const addLayer = () => apply((prev) => ({ ...prev, layers: [...prev.layers, []] }));

  const removeWorkflow = (workflowId: string) =>
    apply((prev) => ({
      layers: prev.layers.map((l) => l.filter((w) => w !== workflowId)),
      staging: prev.staging.filter((w) => w !== workflowId),
    }));

  const addWorkflows = (ids: string[], target: AddTarget) =>
    apply((prev) => {
      const existing = new Set([...prev.layers.flat(), ...prev.staging]);
      const fresh = ids.filter((id) => !existing.has(id));
      if (fresh.length === 0) return prev;
      if (target.kind === 'staging') {
        return { ...prev, staging: [...prev.staging, ...fresh] };
      }
      return {
        ...prev,
        layers: prev.layers.map((l, i) => (i === target.index ? [...l, ...fresh] : l)),
      };
    });

  const handleDrop = ({
    item,
    fromColumnId,
    toColumnId,
    beforeItemId,
  }: BoardDropEvent<string, TestSuiteMemberDetail>) => {
    const wfId = item.workflowId;
    apply((prev) => {
      const layers = prev.layers.map((l) => [...l]);
      let staging = [...prev.staging];
      const without = (arr: string[]) => arr.filter((id) => id !== wfId);
      const insert = (arr: string[]) => {
        const at = beforeItemId ? arr.indexOf(beforeItemId) : -1;
        if (at >= 0) arr.splice(at, 0, wfId);
        else arr.push(wfId);
        return arr;
      };

      // Pull from source.
      if (fromColumnId === STAGING) staging = without(staging);
      else {
        const fi = Number(fromColumnId);
        if (layers[fi]) layers[fi] = without(layers[fi]);
      }
      // Insert into target (matches the Board's drop preview, so no jump).
      if (toColumnId === STAGING) staging = insert(staging);
      else {
        const ti = Number(toColumnId);
        if (!layers[ti]) return prev;
        layers[ti] = insert(layers[ti]);
      }
      return { layers, staging };
    });
  };

  // ── Arrange shortcuts — re-lay-out the whole suite (layers + staging) ─────
  const allIds = React.useMemo(() => [...present.layers.flat(), ...present.staging], [present]);
  const arrangeParallel = () => apply({ layers: allIds.length > 0 ? [allIds] : [[]], staging: [] });
  const arrangeSequential = () =>
    apply({ layers: allIds.length > 0 ? allIds.map((id) => [id]) : [[]], staging: [] });

  // ── Settings dirty (tracked separately from the layout history) ──────────
  const settingsDirty =
    name.trim() !== (suiteQuery.data?.name ?? '') ||
    failureMode !== (suiteQuery.data?.failureMode ?? 'continue') ||
    description !== (suiteQuery.data?.description ?? '');
  const needsSave = layoutDirty || settingsDirty;

  const save = async () => {
    try {
      // Only persist members when the layout actually changed.
      if (layoutDirty) {
        // Drop empty layers so layerIndex stays dense.
        const compact = present.layers.filter((l) => l.length > 0);
        await saveMembers.mutateAsync({ layers: compact, staging: present.staging });
      }
      if (settingsDirty) {
        const trimmedName = name.trim();
        await updateSuite.mutateAsync({
          name: trimmedName || undefined,
          failureMode,
          description: description.trim() ? description : null,
        });
      }
      markSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save suite');
      throw err;
    }
  };

  const handleRun = async () => {
    if (needsSave) {
      try {
        await save();
      } catch {
        return; // save() already surfaced the error
      }
    }
    setRunDialogOpen(true);
  };

  const confirmRun = async (reEnableDegraded: boolean) => {
    try {
      const result = await runSuite.mutateAsync({ id: suiteId, reEnableDegraded });
      setRunDialogOpen(false);
      // This page only ever renders native suites (external ones branch to
      // their own config page above), but the result is discriminated and the
      // compiler is right to insist — an external run has no `executionId`.
      if (result.kind === 'external') {
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
        return;
      }
      navigate({
        to: '/runs/test/$executionId',
        params: { executionId: result.executionId },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to run suite');
    }
  };

  // Cmd/Ctrl+Z → undo layout (ignore while typing in the settings overlay).
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!canUndo) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canUndo, undo]);

  if (suiteQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  // An external suite has no layers to arrange — its contents are its
  // repository — so the canvas is replaced wholesale rather than degraded into
  // an empty board with disabled controls. The hooks above still run (they must,
  // unconditionally), and simply resolve to nothing for a memberless suite.
  if (suiteQuery.data?.kind === 'external' && suiteQuery.data.externalRunner) {
    return (
      <ExternalSuiteConfigPage suite={suiteQuery.data} runner={suiteQuery.data.externalRunner} />
    );
  }

  const layeredCount = present.layers.flat().length;
  const totalWorkflows = layeredCount + present.staging.length;
  const saving = saveMembers.isPending || updateSuite.isPending;

  const stagingColumn: BoardColumnConfig<string> = {
    id: STAGING,
    label: <StagingHeader count={present.staging.length} />,
    headerExtra: (
      <Button
        size="sm"
        variant="ghost"
        iconLeft={<Plus size={14} />}
        onPress={() => setPickerTarget({ kind: 'staging' })}
      >
        Add
      </Button>
    ),
    emptyLabel: 'Drag tests here to stage them — they won’t run until added to a layer',
  };

  const layerColumns: BoardColumnConfig<string>[] = present.layers.map((layer, i) => ({
    id: String(i),
    label: <LayerHeader index={i} count={layer.length} seeds={layerSeedsByLayer.get(i)} />,
    headerExtra: (
      <>
        <Button
          size="sm"
          variant="ghost"
          iconLeft={<Plus size={14} />}
          onPress={() => setPickerTarget({ kind: 'layer', index: i })}
        >
          Add
        </Button>
        {present.layers.length > 1 && (
          <Button
            size="sm"
            variant="icon"
            iconOnly
            tooltip="Remove layer"
            aria-label={`Remove layer ${i + 1}`}
            onPress={() => {
              // Removing a populated layer drops those tests from the suite —
              // confirm first. Empty layers are removed immediately.
              if (layer.length > 0) setConfirmDeleteLayer(i);
              else removeLayer(i);
            }}
          >
            <X size={14} />
          </Button>
        )}
      </>
    ),
    emptyLabel: 'Drag tests here or use Add',
  }));

  const columns: BoardColumnConfig<string>[] = [stagingColumn, ...layerColumns];

  const targetLabel =
    pickerTarget?.kind === 'staging'
      ? 'staging'
      : pickerTarget
        ? `Layer ${pickerTarget.index + 1}`
        : '';

  return (
    <div className="relative flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
      {/* Subhead — canvas toolbar */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-4 py-3">
        <Button
          variant="icon"
          size="sm"
          tooltip="Suite settings"
          aria-label="Suite settings"
          onPress={() => setSettingsOpen(true)}
        >
          <Gear size={16} />
        </Button>
        <Text weight="semibold" className="truncate text-base">
          {name || 'Untitled suite'}
        </Text>
        <Text size="xs" className="hidden shrink-0 text-muted-foreground sm:block">
          {totalWorkflows} test{totalWorkflows === 1 ? '' : 's'} · {present.layers.length} layer
          {present.layers.length === 1 ? '' : 's'}
          {present.staging.length > 0 ? ` · ${present.staging.length} staged` : ''}
        </Text>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* Arrange shortcuts */}
          <div className="hidden items-center gap-1 sm:flex">
            <Button
              size="sm"
              variant="icon"
              iconOnly
              tooltip="Parallel — all tests in one layer"
              aria-label="Arrange all tests into one parallel layer"
              isDisabled={totalWorkflows === 0}
              onPress={arrangeParallel}
            >
              <SquaresFour size={16} />
            </Button>
            <Button
              size="sm"
              variant="icon"
              iconOnly
              tooltip="Sequential — one layer per test"
              aria-label="Arrange all tests one per layer"
              isDisabled={totalWorkflows === 0}
              onPress={arrangeSequential}
            >
              <Rows size={16} />
            </Button>
          </div>
          <ScheduleSuiteModal suiteId={suiteId} suiteName={name || undefined} />
          <Button
            size="sm"
            variant="icon"
            iconOnly
            tooltip="Undo"
            aria-label="Undo layout change"
            isDisabled={!canUndo}
            onPress={undo}
          >
            <ArrowUUpLeft size={16} />
          </Button>
          <Button
            variant="secondary"
            iconLeft={<FloppyDisk size={16} />}
            isDisabled={!needsSave || saving}
            onPress={() => void save()}
          >
            Save
          </Button>
          <Button
            variant="primary"
            iconLeft={<Play size={16} weight="fill" />}
            isDisabled={layeredCount === 0 || runSuite.isPending}
            onPress={() => void handleRun()}
          >
            Run
          </Button>
        </div>
      </div>

      {/* Canvas — flush staging rail on the left, layer bands filling the rest */}
      <div className="flex min-h-0 flex-1">
        <Board<string, TestSuiteMemberDetail>
          orientation="rows"
          railColumnId={STAGING}
          height="100%"
          columns={columns}
          itemsByColumn={itemsByColumn}
          getItemId={(m) => m.workflowId}
          getItemColumnId={(m) => columnOfWorkflow.get(m.workflowId) ?? STAGING}
          onDrop={handleDrop}
          renderCard={(member, state) => (
            <SuiteTestCard
              member={member}
              flowHref={`/flows/${member.workflowId}`}
              onRemove={() => removeWorkflow(member.workflowId)}
              isOverlay={state.isOverlay}
            />
          )}
          header={
            suiteSeeds.length > 0 ? (
              <ReadOnlyBand
                pill="Seed"
                hint="Provisioned once before the suite runs"
                items={suiteSeeds}
              />
            ) : undefined
          }
          footer={
            <>
              {suiteTeardowns.length > 0 && (
                <ReadOnlyBand
                  pill="Teardown"
                  hint="Runs after the suite completes"
                  items={suiteTeardowns}
                />
              )}
              <div>
                <Button variant="secondary" iconLeft={<Plus size={16} />} onPress={addLayer}>
                  Add layer
                </Button>
              </div>
            </>
          }
        />
      </div>

      {/* Fly-up settings */}
      <AnimatePresence>
        {settingsOpen && (
          <SuiteSettingsOverlay
            name={name}
            description={description}
            failureMode={failureMode}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onFailureModeChange={setFailureMode}
            onClose={() => setSettingsOpen(false)}
            onSave={async () => {
              await save();
              setSettingsOpen(false);
            }}
            isSaving={saving}
          />
        )}
      </AnimatePresence>

      <AddTestsSearch
        isOpen={pickerTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPickerTarget(null);
        }}
        targetLabel={targetLabel}
        testOptions={testOptions}
        tagOptions={tagOptions}
        onAdd={(ids) => {
          if (pickerTarget) addWorkflows(ids, pickerTarget);
        }}
      />

      {/* Destructive confirm for removing a populated layer. */}
      <DialogTrigger
        isOpen={confirmDeleteLayer !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteLayer(null);
        }}
        size="md"
      >
        <span />
        <Dialog
          title={
            confirmDeleteLayer !== null
              ? `Remove Layer ${confirmDeleteLayer + 1}?`
              : 'Remove layer?'
          }
        >
          <DialogContent>
            <Flex align="start" gap="md" className="py-2">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <Warning size={22} weight="fill" className="text-destructive" />
              </span>
              <Text size="sm" className="leading-relaxed text-muted-foreground">
                {confirmDeleteLayer !== null && (
                  <>
                    You&rsquo;ll remove{' '}
                    <Text as="span" weight="semibold" className="text-foreground">
                      {present.layers[confirmDeleteLayer]?.length ?? 0} test
                      {(present.layers[confirmDeleteLayer]?.length ?? 0) === 1 ? '' : 's'}
                    </Text>{' '}
                    from this suite. The tests themselves aren&rsquo;t deleted — they just leave the
                    suite.
                  </>
                )}
              </Text>
            </Flex>
          </DialogContent>
          <DialogFooter className="mt-6">
            <Button variant="secondary" onPress={() => setConfirmDeleteLayer(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={() => {
                if (confirmDeleteLayer !== null) removeLayer(confirmDeleteLayer);
                setConfirmDeleteLayer(null);
              }}
            >
              Remove layer
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      <RunNativeSuiteDialog
        isOpen={runDialogOpen}
        onOpenChange={setRunDialogOpen}
        suiteName={suiteQuery.data?.name ?? 'suite'}
        members={runMembers}
        isLoading={suiteQuery.isLoading}
        isPending={runSuite.isPending}
        onConfirm={(reEnableDegraded) => void confirmRun(reEnableDegraded)}
      />
    </div>
  );
}
